import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Code, Fire, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";

import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const CATEGORIES = ["All", "Code Completion", "Code Review", "Testing", "Debugging", "Documentation", "Full IDE", "Terminal AI", "Agent", "IDE", "IDE Plugin", "CLI / Agent", "Autonomous Agent"];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-3 mb-4">
        <div className="w-10 h-10 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-2 bg-slate-200 rounded w-full mb-4" />
      <div className="grid grid-cols-2 gap-1">
        {[1,2,3,4].map(i => <div key={i} className="h-3 bg-slate-200 rounded" />)}
      </div>
    </div>
  );
}

function CodingToolCard({ tool }) {
  const score = tool.score || tool.trend_score || 75;
  const slug = tool.slug || tool.id;
  return (
    <Link to={`/trending-ai/coding/${slug}`} className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{tool.emoji || "⚡"}</span>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(tool.name)}</h3>
              {(tool.isNew || tool.is_new) && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
              {(tool.hot || tool.isHot) && (
                <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Fire size={7} weight="fill" /> HOT
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">{tool.by || tool.maker} · {tool.category}</p>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-2xl font-black text-blue-600">{score}</div>
          <div className="text-[10px] text-slate-400">trend score</div>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-4">{stripHtml(tool.description)}</p>

      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
          <span>Trend Score</span>
          <span>{score}/100</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {tool.highlights && tool.highlights.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Highlights</p>
          <div className="grid grid-cols-2 gap-1">
            {tool.highlights.map((h) => (
              <span key={h} className="flex items-center gap-1 text-[10px] text-slate-600">
                <CheckCircle size={9} className="text-emerald-500 shrink-0" weight="fill" /> {h}
              </span>
            ))}
          </div>
        </div>
      )}

      {tool.tags && tool.tags.length > 0 && !tool.highlights && (
        <div className="flex flex-wrap gap-1 mb-4">
          {tool.tags.map((t) => (
            <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <div>
          <span className="text-xs font-medium text-slate-700">{tool.pricing || tool.price}</span>
          {tool.users && <p className="text-[10px] text-slate-400">{tool.users} users</p>}
        </div>
        {(tool.website || tool.url) && (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
            Visit site <ArrowUpRight size={11} />
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AICodingPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");

  const fetchFn = useCallback(
    (page) => fetchAISection("coding", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-coding",
    getCacheKey: (page) => `ai-coding-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((t) => {
      const matchCat = cat === "All" || t.category === cat;
      const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || (t.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => (b.score || b.trend_score || 0) - (a.score || a.trend_score || 0));

  return (
    <>
      <SEO
        title="AI Coding Tools — Best AI Tools for Developers · TrendMing"
        description="The best AI coding tools: Cursor, GitHub Copilot, Claude Code, Devin, v0, Bolt.new. Compare editors, agents, and AI-powered development tools."
        canonical="https://trendming.com/trending-ai/coding"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Coding</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Coding Tools</h1>
          <p className="text-slate-500 text-sm mt-1">The definitive guide to AI tools for developers — editors, agents, code generators, and autonomous coding systems.</p>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 text-white mb-6">
          <div className="flex items-start gap-4">
            <Code size={24} className="shrink-0 mt-0.5" weight="fill" />
            <div>
              <h2 className="font-bold text-base mb-1">The AI Coding Revolution</h2>
              <p className="text-blue-100 text-sm leading-relaxed">
                AI coding tools are now used by 70% of professional developers. Cursor hit $9.9B valuation.
                Devin can autonomously ship full applications. The AI code editor wars are just beginning.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search coding tools…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((tool) => <CodingToolCard key={tool.id || tool.slug} tool={tool} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No tools found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load coding tools</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500 mb-3">Looking for more AI tools?</p>
          <Link
            to="/ai-tools?tab=coding-ai"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-sm rounded-xl transition-colors"
          >
            Browse all Coding AI tools in our directory <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>
    </>
  );
}
